/**
 * Single run path for CSV citizenship Import/Export profiles.
 * Manual "Run now" and scheduled ambient jobs both call these functions.
 */

import { parseCSV } from '../../arrange/import';
import { getSupabaseClient } from '../supabaseClient';
import { getCurrentWorkspaceId } from '../supabaseSync';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import type { FieldMappingEntry } from '../fieldMappingRegistry';
import {
  appendMissingFromFile,
  classifyCsvDeltaRows,
  summarizeCsvDelta,
  type CsvDeltaExistingItem,
} from './csvDeltaEngine';
import { applyCsvCitizenshipImport } from './csvCitizenshipImport';
import { buildCitizenshipExportCsv } from './csvExport';
import {
  getCsvCitizenshipProfile,
  storeCsvProfileUpload,
} from './csvProfilesService';
import type {
  CsvCitizenshipFieldDiff,
  CsvCitizenshipRunCounts,
  CsvCitizenshipRunRecord,
  CsvRunTrigger,
} from './csvCitizenshipTypes';
import { snapshotComparableFields } from './csvDeltaEngine';
import type { Item } from '../../types/prioritization/lifecycle';

function itemToExisting(item: Item): CsvDeltaExistingItem {
  const tags = Array.isArray(item.tags) ? item.tags : [];
  return {
    id: item.id,
    external_id: item.external_id ?? null,
    fields: snapshotComparableFields({
      title: item.title,
      description: item.description,
      itemCategory: item.itemCategory,
      metaCategory: item.metaCategory,
      strategicBucket: item.strategicBucket,
      altitudeLevel: item.altitudeLevel,
      source: item.source,
      tags,
      desiredOutcome: item.desiredOutcome,
      beneficiary: item.beneficiary,
      evidenceOfSuccess: item.evidenceOfSuccess,
      whyNow: item.whyNow,
      obstacles: item.obstacles,
      work_type: item.work_type,
      launch_type: item.launch_type,
      investment_type: item.investment_type,
      exit_status: item.exit_status,
      parentItemId: item.parentItemId,
    }),
  };
}

function mapRawToInternal(
  rawRows: Record<string, unknown>[],
  entries: FieldMappingEntry[],
  defaults?: Record<string, string>,
): Record<string, unknown>[] {
  const importEntries = entries.filter(
    (e) => e.enabled && (e.direction === 'import' || e.direction === 'both'),
  );
  return rawRows.map((raw) => {
    const out: Record<string, unknown> = { ...(defaults ?? {}) };
    for (const e of importEntries) {
      const v = raw[e.externalFieldKey];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        out[e.internalFieldKey] = v;
      }
    }
    return out;
  });
}

async function writeImportLog(params: {
  workspaceId: string;
  actorUserId?: string | null;
  profileId: string;
  profileName: string;
  trigger: CsvRunTrigger;
  counts: CsvCitizenshipRunCounts;
  fieldDiffs: CsvCitizenshipFieldDiff[];
  missingKeys: CsvCitizenshipRunRecord['missingKeys'];
  rowCount: number;
  status: 'completed' | 'failed' | 'partial';
  errorSummary?: string | null;
}): Promise<string | null> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('import_logs')
      .insert({
        workspace_id: params.workspaceId,
        actor_user_id: params.actorUserId ?? null,
        entity_type: 'items',
        source_type: 'csv_citizenship',
        source_name: params.profileName,
        row_count: params.rowCount,
        inserted_count: params.counts.new,
        updated_count: params.counts.changed,
        failed_count: params.counts.failed + params.counts.queued,
        unchanged_count: params.counts.unchanged,
        clarify_count: params.counts.queued,
        missing_count: params.counts.missing,
        trigger: params.trigger,
        profile_id: params.profileId,
        disposition_detail: {
          fieldDiffs: params.fieldDiffs,
          missingKeys: params.missingKeys,
          counts: params.counts,
        },
        status: params.status,
        error_summary: params.errorSummary ?? null,
        completed_at: new Date().toISOString(),
      })
      .select('id')
      .single();
    if (error) {
      console.warn('[csvProfileRunner] import_logs write failed:', error.message);
      return null;
    }
    return (data?.id as string) ?? null;
  } catch (err) {
    console.warn('[csvProfileRunner] import_logs write failed:', err);
    return null;
  }
}

export async function listCsvCitizenshipRuns(
  workspaceId: string,
  profileId?: string,
  limit = 40,
): Promise<CsvCitizenshipRunRecord[]> {
  let q = getSupabaseClient()
    .from('import_logs')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('source_type', 'csv_citizenship')
    .order('started_at', { ascending: false })
    .limit(limit);
  if (profileId) q = q.eq('profile_id', profileId);
  const { data, error } = await q;
  if (error || !data) return [];
  return data.map((row) => {
    const detail = (row.disposition_detail ?? {}) as {
      fieldDiffs?: CsvCitizenshipFieldDiff[];
      missingKeys?: CsvCitizenshipRunRecord['missingKeys'];
      counts?: CsvCitizenshipRunCounts;
    };
    return {
      id: row.id as string,
      workspaceId,
      profileId: (row.profile_id as string) ?? '',
      profileName: (row.source_name as string) ?? '',
      kind: 'import',
      trigger: ((row.trigger as string) === 'scheduled' ? 'scheduled' : 'manual') as CsvRunTrigger,
      startedAt: row.started_at as string,
      completedAt: (row.completed_at as string) ?? undefined,
      status: row.status as CsvCitizenshipRunRecord['status'],
      counts: detail.counts ?? {
        new: row.inserted_count ?? 0,
        changed: row.updated_count ?? 0,
        unchanged: row.unchanged_count ?? 0,
        queued: row.clarify_count ?? 0,
        missing: row.missing_count ?? 0,
        failed: row.failed_count ?? 0,
      },
      fieldDiffs: detail.fieldDiffs ?? [],
      missingKeys: detail.missingKeys ?? [],
      errorSummary: (row.error_summary as string) ?? null,
    };
  });
}

export async function runCsvImportProfile(params: {
  workspaceId?: string;
  profileId: string;
  trigger: CsvRunTrigger;
  actorUserId?: string | null;
  /** When provided (manual upload), used and stored on the profile. */
  fileText?: string;
  fileName?: string;
}): Promise<{
  runId: string | null;
  counts: CsvCitizenshipRunCounts;
  fieldDiffs: CsvCitizenshipFieldDiff[];
  summaryLabel: string;
  error: string | null;
}> {
  const workspaceId = params.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId) {
    return {
      runId: null,
      counts: { new: 0, changed: 0, unchanged: 0, queued: 0, missing: 0, failed: 0 },
      fieldDiffs: [],
      summaryLabel: 'No workspace',
      error: 'No workspace in scope',
    };
  }

  const profile = await getCsvCitizenshipProfile(workspaceId, params.profileId);
  if (!profile || profile.meta.kind !== 'import') {
    return {
      runId: null,
      counts: { new: 0, changed: 0, unchanged: 0, queued: 0, missing: 0, failed: 0 },
      fieldDiffs: [],
      summaryLabel: 'Profile not found',
      error: 'Import profile not found',
    };
  }

  let fileText = params.fileText ?? null;
  if (!fileText && profile.meta.source === 'url' && profile.meta.sourceUrl) {
    try {
      const res = await fetch(profile.meta.sourceUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fileText = await res.text();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fetch failed';
      const counts = { new: 0, changed: 0, unchanged: 0, queued: 0, missing: 0, failed: 1 };
      const runId = await writeImportLog({
        workspaceId,
        actorUserId: params.actorUserId,
        profileId: profile.set.id,
        profileName: profile.set.name,
        trigger: params.trigger,
        counts,
        fieldDiffs: [],
        missingKeys: [],
        rowCount: 0,
        status: 'failed',
        errorSummary: `Could not fetch source URL: ${msg}`,
      });
      return { runId, counts, fieldDiffs: [], summaryLabel: msg, error: msg };
    }
  }
  if (!fileText) fileText = profile.meta.lastFileText ?? null;
  if (!fileText) {
    const msg = 'No CSV file available. Upload a file or set a source URL on the profile.';
    const counts = { new: 0, changed: 0, unchanged: 0, queued: 0, missing: 0, failed: 1 };
    const runId = await writeImportLog({
      workspaceId,
      actorUserId: params.actorUserId,
      profileId: profile.set.id,
      profileName: profile.set.name,
      trigger: params.trigger,
      counts,
      fieldDiffs: [],
      missingKeys: [],
      rowCount: 0,
      status: 'failed',
      errorSummary: msg,
    });
    return { runId, counts, fieldDiffs: [], summaryLabel: msg, error: msg };
  }

  if (params.fileText) {
    await storeCsvProfileUpload({
      workspaceId,
      profileId: profile.set.id,
      fileName: params.fileName ?? 'upload.csv',
      fileText: params.fileText,
    });
  }

  const rawRows = parseCSV(fileText, true) as Record<string, unknown>[];
  const mapped = mapRawToInternal(rawRows, profile.set.entries, profile.meta.defaults);

  // Prefetch classification for field diffs + missing before apply
  const existing = useItemsStore.getState().items.map(itemToExisting);
  const classified = appendMissingFromFile(
    classifyCsvDeltaRows(mapped, existing, {
      externalKeyField: profile.meta.externalKeyField ?? 'external_id',
    }),
    existing,
  );
  const summary = summarizeCsvDelta(classified);

  const fieldDiffs: CsvCitizenshipFieldDiff[] = classified
    .filter((r) => r.disposition === 'update' && (r.changedFields?.length ?? 0) > 0)
    .map((r) => ({
      externalId: r.externalId,
      itemId: r.matchItemId,
      title: String(r.row.title ?? ''),
      changedFields: r.changedFields ?? [],
      before: r.beforeFields,
      after: r.afterFields,
    }));

  const missingKeys = classified
    .filter((r) => r.disposition === 'missing')
    .map((r) => ({
      externalId: r.externalId ?? '',
      itemId: r.matchItemId ?? '',
      title: (r.row.title as string) ?? null,
    }));

  // Apply only non-missing rows (missing is flag-only)
  const applyRows = mapped; // applyCsvCitizenshipImport re-classifies; missing not in file so OK
  const applyResult = await applyCsvCitizenshipImport(applyRows, {
    profileName: profile.set.name,
    defaults: profile.meta.defaults,
    externalKeyField: profile.meta.externalKeyField,
  });

  const counts: CsvCitizenshipRunCounts = {
    new: applyResult.created,
    changed: applyResult.updated,
    unchanged: applyResult.unchanged,
    queued: applyResult.clarified,
    missing: summary.missing,
    failed: applyResult.failed,
  };

  const status: 'completed' | 'partial' | 'failed' =
    counts.failed > 0 && counts.new + counts.changed === 0
      ? 'failed'
      : counts.queued > 0 || counts.failed > 0
        ? 'partial'
        : 'completed';

  const runId = await writeImportLog({
    workspaceId,
    actorUserId: params.actorUserId,
    profileId: profile.set.id,
    profileName: profile.set.name,
    trigger: params.trigger,
    counts,
    fieldDiffs,
    missingKeys,
    rowCount: mapped.length,
    status,
  });

  return {
    runId,
    counts,
    fieldDiffs,
    summaryLabel: `${applyResult.summaryLabel}${summary.missing ? `, ${summary.missing} missing-from-file` : ''}`,
    error: null,
  };
}

export async function runCsvExportProfile(params: {
  workspaceId?: string;
  profileId: string;
  trigger: CsvRunTrigger;
  actorUserId?: string | null;
}): Promise<{
  runId: string | null;
  csv: string;
  rowCount: number;
  columns: string[];
  error: string | null;
}> {
  const workspaceId = params.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId) {
    return { runId: null, csv: '', rowCount: 0, columns: [], error: 'No workspace in scope' };
  }

  const profile = await getCsvCitizenshipProfile(workspaceId, params.profileId);
  if (!profile || profile.meta.kind !== 'export') {
    return { runId: null, csv: '', rowCount: 0, columns: [], error: 'Export profile not found' };
  }

  const items = useItemsStore.getState().items;
  const built = buildCitizenshipExportCsv(items, profile.meta, profile.set.entries);

  if (profile.meta.destination === 'url' && profile.meta.destinationUrl) {
    try {
      await fetch(profile.meta.destinationUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv; charset=utf-8' },
        body: built.csv,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Destination POST failed';
      const runId = await writeImportLog({
        workspaceId,
        actorUserId: params.actorUserId,
        profileId: profile.set.id,
        profileName: profile.set.name,
        trigger: params.trigger,
        counts: { new: 0, changed: 0, unchanged: built.rowCount, queued: 0, missing: 0, failed: 1 },
        fieldDiffs: [],
        missingKeys: [],
        rowCount: built.rowCount,
        status: 'failed',
        errorSummary: msg,
      });
      return { runId, csv: built.csv, rowCount: built.rowCount, columns: built.columns, error: msg };
    }
  }

  const runId = await writeImportLog({
    workspaceId,
    actorUserId: params.actorUserId,
    profileId: profile.set.id,
    profileName: `[export] ${profile.set.name}`,
    trigger: params.trigger,
    counts: {
      new: 0,
      changed: 0,
      unchanged: built.rowCount,
      queued: 0,
      missing: 0,
      failed: 0,
    },
    fieldDiffs: [],
    missingKeys: [],
    rowCount: built.rowCount,
    status: 'completed',
  });

  // Stash last export on profile for ambient delivery / download history
  // (download is handled by UI from returned csv)

  return {
    runId,
    csv: built.csv,
    rowCount: built.rowCount,
    columns: built.columns,
    error: null,
  };
}
